import { useState, useRef, useCallback } from 'react';
import { 
  Car, Upload, Loader2, CheckCircle, X, AlertCircle, Camera, 
  Gauge, Fuel, Settings, Calendar, Palette, FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Emirates list
const emirates = [
  { id: 'dubai', ar: 'دبي', en: 'Dubai' },
  { id: 'abudhabi', ar: 'أبوظبي', en: 'Abu Dhabi' },
  { id: 'sharjah', ar: 'الشارقة', en: 'Sharjah' },
  { id: 'ajman', ar: 'عجمان', en: 'Ajman' },
  { id: 'rak', ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
  { id: 'fujairah', ar: 'الفجيرة', en: 'Fujairah' },
  { id: 'uaq', ar: 'أم القيوين', en: 'Umm Al Quwain' }
];

// Body types
const bodyTypes = [
  { id: 'sedan', ar: 'سيدان', en: 'Sedan' },
  { id: 'suv', ar: 'دفع رباعي SUV', en: 'SUV' },
  { id: 'hatchback', ar: 'هاتشباك', en: 'Hatchback' },
  { id: 'coupe', ar: 'كوبيه', en: 'Coupe' },
  { id: 'convertible', ar: 'مكشوف', en: 'Convertible' },
  { id: 'pickup', ar: 'بيك أب', en: 'Pick Up' },
  { id: 'van', ar: 'فان', en: 'Van' },
  { id: 'wagon', ar: 'ستيشن واغن', en: 'Wagon' }
];

// Fuel types
const fuelTypes = [
  { id: 'petrol', ar: 'بنزين', en: 'Petrol' },
  { id: 'diesel', ar: 'ديزل', en: 'Diesel' },
  { id: 'electric', ar: 'كهربائي', en: 'Electric' },
  { id: 'hybrid', ar: 'هايبرد', en: 'Hybrid' }
];

// Transmission types
const transmissionTypes = [
  { id: 'automatic', ar: 'أوتوماتيك', en: 'Automatic' },
  { id: 'manual', ar: 'يدوي', en: 'Manual' }
];

// Condition options
const conditionOptions = [
  { id: 'excellent', ar: 'ممتازة', en: 'Excellent' },
  { id: 'good', ar: 'جيدة', en: 'Good' },
  { id: 'fair', ar: 'متوسطة', en: 'Fair' }
];

// Regional specs
const regionalSpecs = [
  { id: 'gcc', ar: 'خليجي', en: 'GCC Specs' },
  { id: 'american', ar: 'أمريكي', en: 'American Specs' },
  { id: 'european', ar: 'أوروبي', en: 'European Specs' },
  { id: 'japanese', ar: 'ياباني', en: 'Japanese Specs' },
  { id: 'other', ar: 'أخرى', en: 'Other' }
];

export default function CarStoreForm({ onDataChange, initialData = {} }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  
  // Form data
  const [formData, setFormData] = useState({
    make: initialData.make || '',
    model: initialData.model || '',
    year: initialData.year || '',
    color: initialData.color || '',
    bodyType: initialData.bodyType || '',
    fuelType: initialData.fuelType || '',
    transmission: initialData.transmission || '',
    mileage: initialData.mileage || '',
    condition: initialData.condition || '',
    regionalSpecs: initialData.regionalSpecs || '',
    emirate: initialData.emirate || '',
    price: initialData.price || '',
    description: initialData.description || '',
    ...initialData
  });
  
  // AI Analysis states
  const [carImages, setCarImages] = useState([]);
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [imageAnalysisResult, setImageAnalysisResult] = useState(null);
  const [rejectedImage, setRejectedImage] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const imageInputRef = useRef(null);
  
  // Handle form field change
  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange?.(newData, carImages);
  };
  
  // Notify parent when images change
  const updateImagesAndNotify = (newImages) => {
    setCarImages(newImages);
    onDataChange?.(formData, newImages);
  };
  
  // Handle image upload and AI analysis
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 3;
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(language === 'ar' ? `حجم الملف يجب أن يكون أقل من ${maxSize} ميجابايت` : `File size must be less than ${maxSize}MB`);
      return;
    }
    
    // Reset states
    setRejectedImage(false);
    setRejectionReason('');
    setImageAnalysisResult(null);
    setAnalyzingImage(true);
    
    const token = localStorage.getItem('business_token');
    if (!token) {
      toast.error(language === 'ar' ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      setAnalyzingImage(false);
      return;
    }
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/ai/analyze-car-image`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status === 'rejected') {
        setRejectedImage(true);
        setRejectionReason(response.data.message);
        toast.error(response.data.message);
      } else if (response.data.status === 'success' && response.data.data) {
        const data = response.data.data;
        setImageAnalysisResult(data);
        
        // Create preview and update images
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImages = [...carImages, {
            file,
            preview: reader.result,
            type: data.image_type
          }];
          updateImagesAndNotify(newImages);
        };
        reader.readAsDataURL(file);
        
        // Auto-fill form fields from AI analysis
        const newData = { ...formData };
        if (data.make) newData.make = data.make;
        if (data.model) newData.model = data.model;
        if (data.year) newData.year = data.year;
        if (data.color) newData.color = data.color;
        if (data.body_type) newData.bodyType = data.body_type;
        if (data.fuel_type) newData.fuelType = data.fuel_type;
        if (data.transmission) newData.transmission = data.transmission;
        if (data.mileage) newData.mileage = data.mileage;
        if (data.condition) newData.condition = data.condition;
        
        setFormData(newData);
        onDataChange?.(newData, carImages);
        
        toast.success(language === 'ar' ? 'تم تحليل الصورة وتعبئة البيانات تلقائياً!' : 'Image analyzed and data auto-filled!');
      }
    } catch (error) {
      console.error('Image analysis error:', error);
      toast.error(language === 'ar' ? 'فشل في تحليل الصورة' : 'Failed to analyze image');
    } finally {
      setAnalyzingImage(false);
    }
  };
  
  // Remove image
  const removeImage = (index) => {
    const newImages = carImages.filter((_, i) => i !== index);
    updateImagesAndNotify(newImages);
  };
  
  return (
    <div className="space-y-6">
      {/* Car Images Section with AI Analysis */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-green-50 border-green-200'}`}>
        <Label className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Camera className="h-5 w-5 text-green-600" />
          {language === 'ar' ? 'صور السيارة (تحليل بالذكاء الاصطناعي)' : 'Car Images (AI Analysis)'}
        </Label>
        
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-green-700'}`}>
          {language === 'ar' 
            ? 'ارفع صور السيارة وسيقوم الذكاء الاصطناعي بالتحقق منها وتعبئة البيانات تلقائياً'
            : 'Upload car images and AI will verify them and auto-fill the data'
          }
        </p>
        
        {/* Uploaded Images */}
        {carImages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {carImages.map((img, idx) => (
              <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border-2 border-green-400">
                <img src={img.preview} alt={`Car ${idx + 1}`} className="w-full h-full object-cover" />
                <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {img.type === 'exterior' ? (language === 'ar' ? 'خارجي' : 'Exterior') : (language === 'ar' ? 'داخلي' : 'Interior')}
                </div>
                <button 
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 left-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Upload Area */}
        <div 
          onClick={() => !analyzingImage && imageInputRef.current?.click()}
          className={`w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
            analyzingImage ? 'opacity-50 cursor-wait' : ''
          } ${isDarkMode ? 'border-[#475569] hover:border-green-500' : 'border-green-300 hover:border-green-500'}`}
          data-testid="car-image-upload"
        >
          {analyzingImage ? (
            <>
              <Loader2 className="h-10 w-10 text-green-500 animate-spin mb-3" />
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-green-700'}`}>
                {language === 'ar' ? 'جاري تحليل الصورة بالذكاء الاصطناعي...' : 'Analyzing image with AI...'}
              </p>
            </>
          ) : (
            <>
              <Upload className="h-10 w-10 text-green-500 mb-3" />
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-green-700'}`}>
                {language === 'ar' ? 'اضغط لرفع صورة السيارة' : 'Click to upload car image'}
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>
                {language === 'ar' ? 'صور داخلية وخارجية - PNG, JPG' : 'Interior & exterior images - PNG, JPG'}
              </p>
            </>
          )}
        </div>
        <input 
          ref={imageInputRef} 
          type="file" 
          accept="image/*" 
          onChange={handleImageUpload} 
          className="hidden" 
        />
        
        {/* Rejected Image Warning */}
        {rejectedImage && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <div>
                <p className="font-bold text-red-700">
                  {language === 'ar' ? 'الصورة غير صالحة' : 'Invalid Image'}
                </p>
                <p className="text-sm text-red-600">{rejectionReason}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 w-full border-red-300 text-red-600 hover:bg-red-100"
              onClick={() => {
                setRejectedImage(false);
                setRejectionReason('');
                imageInputRef.current?.click();
              }}
            >
              {language === 'ar' ? 'رفع صورة أخرى' : 'Upload Another Image'}
            </Button>
          </div>
        )}
        
        {/* AI Analysis Success */}
        {imageAnalysisResult && !rejectedImage && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-bold text-green-700">
                {language === 'ar' ? 'تم تحليل الصورة بنجاح!' : 'Image analyzed successfully!'}
              </span>
            </div>
            {imageAnalysisResult.notes && (
              <p className="text-sm text-green-600 mt-2">
                <span className="font-medium">{language === 'ar' ? 'ملاحظات:' : 'Notes:'}</span> {imageAnalysisResult.notes}
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Basic Information */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Car className="h-5 w-5 text-green-600" />
          {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Make */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الماركة *' : 'Make *'}
            </Label>
            <Input
              value={formData.make}
              onChange={(e) => handleFieldChange('make', e.target.value)}
              placeholder={language === 'ar' ? 'مثال: تويوتا' : 'Example: Toyota'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-make-input"
            />
          </div>
          
          {/* Model */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الموديل *' : 'Model *'}
            </Label>
            <Input
              value={formData.model}
              onChange={(e) => handleFieldChange('model', e.target.value)}
              placeholder={language === 'ar' ? 'مثال: كامري' : 'Example: Camry'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-model-input"
            />
          </div>
          
          {/* Year */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'سنة الصنع *' : 'Year *'}
            </Label>
            <Input
              type="number"
              value={formData.year}
              onChange={(e) => handleFieldChange('year', e.target.value)}
              placeholder="2024"
              min="1990"
              max={new Date().getFullYear() + 1}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-year-input"
            />
          </div>
          
          {/* Color */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Palette className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'اللون *' : 'Color *'}
            </Label>
            <Input
              value={formData.color}
              onChange={(e) => handleFieldChange('color', e.target.value)}
              placeholder={language === 'ar' ? 'مثال: أبيض' : 'Example: White'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-color-input"
            />
          </div>
        </div>
      </div>
      
      {/* Technical Specifications */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Settings className="h-5 w-5 text-blue-600" />
          {language === 'ar' ? 'المواصفات الفنية' : 'Technical Specifications'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Body Type */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'نوع الهيكل *' : 'Body Type *'}
            </Label>
            <select
              value={formData.bodyType}
              onChange={(e) => handleFieldChange('bodyType', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-body-type-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع الهيكل' : 'Select Body Type'}</option>
              {bodyTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Fuel Type */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Fuel className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'نوع الوقود *' : 'Fuel Type *'}
            </Label>
            <select
              value={formData.fuelType}
              onChange={(e) => handleFieldChange('fuelType', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-fuel-type-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع الوقود' : 'Select Fuel Type'}</option>
              {fuelTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Transmission */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'ناقل الحركة *' : 'Transmission *'}
            </Label>
            <select
              value={formData.transmission}
              onChange={(e) => handleFieldChange('transmission', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-transmission-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع القير' : 'Select Transmission'}</option>
              {transmissionTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Mileage */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Gauge className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'عداد المسافة (كم) *' : 'Mileage (km) *'}
            </Label>
            <Input
              type="number"
              value={formData.mileage}
              onChange={(e) => handleFieldChange('mileage', e.target.value)}
              placeholder="50000"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-mileage-input"
            />
          </div>
          
          {/* Condition */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'حالة السيارة *' : 'Condition *'}
            </Label>
            <select
              value={formData.condition}
              onChange={(e) => handleFieldChange('condition', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-condition-select"
            >
              <option value="">{language === 'ar' ? 'اختر الحالة' : 'Select Condition'}</option>
              {conditionOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {language === 'ar' ? opt.ar : opt.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Regional Specs */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'المواصفات الإقليمية *' : 'Regional Specs *'}
            </Label>
            <select
              value={formData.regionalSpecs}
              onChange={(e) => handleFieldChange('regionalSpecs', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-regional-specs-select"
            >
              <option value="">{language === 'ar' ? 'اختر المواصفات' : 'Select Specs'}</option>
              {regionalSpecs.map(spec => (
                <option key={spec.id} value={spec.id}>
                  {language === 'ar' ? spec.ar : spec.en}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Location & Price */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <FileText className="h-5 w-5 text-amber-600" />
          {language === 'ar' ? 'الموقع والسعر' : 'Location & Price'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Emirate */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الإمارة *' : 'Emirate *'}
            </Label>
            <select
              value={formData.emirate}
              onChange={(e) => handleFieldChange('emirate', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="car-emirate-select"
            >
              <option value="">{language === 'ar' ? 'اختر الإمارة' : 'Select Emirate'}</option>
              {emirates.map(em => (
                <option key={em.id} value={em.id}>
                  {language === 'ar' ? em.ar : em.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Price */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'السعر (درهم) *' : 'Price (AED) *'}
            </Label>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => handleFieldChange('price', e.target.value)}
              placeholder="150000"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="car-price-input"
            />
          </div>
        </div>
        
        {/* Description */}
        <div className="mt-4">
          <Label className={isDarkMode ? 'text-gray-300' : ''}>
            {language === 'ar' ? 'وصف إضافي' : 'Additional Description'}
          </Label>
          <textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder={language === 'ar' ? 'معلومات إضافية عن السيارة...' : 'Additional information about the car...'}
            rows={3}
            className={`w-full rounded-md border px-3 py-2 text-sm ${
              isDarkMode 
                ? 'bg-[#0F172A] border-[#334155] text-white' 
                : 'bg-white border-gray-200 text-gray-900'
            }`}
            data-testid="car-description-textarea"
          />
        </div>
      </div>
    </div>
  );
}
